package com.tovtalk.ui.scaffold

import androidx.compose.foundation.layout.RowScope
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarColors
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.text.font.FontWeight
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

/**
 * Preset top bar for TovTalk secondary screens (settings, edit profile, chat detail, etc.).
 *
 * Replaces the hand-rolled `TopAppBar { title / navigationIcon / colors }` block that 17
 * screens currently duplicate. Handles:
 *
 * - Back navigation button (shown only when [onBack] is non-null).
 *   Uses [Icons.AutoMirrored.Filled.ArrowBack] which auto-flips in RTL layouts.
 * - TovTalk theme colors (background = `backgroundPrimary`, text = `textPrimary`).
 * - Bold title styling matching the existing `AboutScreen` / `SettingsScreen` convention.
 * - Optional `centered = true` for screens that prefer `CenterAlignedTopAppBar`.
 *
 * Title is a plain [String] because all 17 audited screens only ever set a text title.
 * A composable-title override can be added later if needed (YAGNI).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TovTalkTopBar(
    title: String,
    onBack: (() -> Unit)? = null,
    actions: @Composable RowScope.() -> Unit = {},
    centered: Boolean = false,
    colors: TopAppBarColors = TopAppBarDefaults.topAppBarColors(
        containerColor = TovTalkTheme.colors.backgroundPrimary,
        titleContentColor = TovTalkTheme.colors.textPrimary,
        navigationIconContentColor = TovTalkTheme.colors.textPrimary,
        actionIconContentColor = TovTalkTheme.colors.textPrimary,
    ),
) {
    val titleContent: @Composable () -> Unit = {
        Text(
            text = title,
            style = TovTalkTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
        )
    }

    val navigationIcon: @Composable () -> Unit = {
        if (onBack != null) {
            IconButton(onClick = onBack) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = StringProvider.get("common_back"),
                )
            }
        }
    }

    if (centered) {
        CenterAlignedTopAppBar(
            title = titleContent,
            navigationIcon = navigationIcon,
            actions = actions,
            colors = colors,
        )
    } else {
        TopAppBar(
            title = titleContent,
            navigationIcon = navigationIcon,
            actions = actions,
            colors = colors,
        )
    }
}
